import { useState } from "react";
import Typography from "@shared/v2/Typography";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import dayjs from "@shared/dayjs";
import useActiveProducts from "../Products/hooks/useActiveProducts";
import { ProductFamilyIds } from "../Products/constants";
import Loading from "../Goals/components/Loader";
import OpportunitiesCancelModal from "../Opportunities/components/OpportunitiesCancelModal";

interface PhoneSystemProps {
  portalUrl: string;
}

const PhoneSystem = ({ portalUrl }: PhoneSystemProps) => {
  const { activeProducts, loading, actions } = useActiveProducts(ProductFamilyIds.Opportunities);
  const activeProduct = activeProducts[0];
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-[300px] tw-items-center tw-justify-center">
        <Loading />
      </div>
    );
  }

  if (!activeProduct) {
    return (
      <div className="tw-min-h-[300px] tw-flex tw-flex-col tw-gap-[12px] tw-items-center">
        <div className="tw-text-11d tw-font-semibold tw-tracking-[1px] tw-uppercase tw-text-gray-50">
          We’re here to make prospecting easy
        </div>
        {/* @ts-expect-error props */}
        <Typography variant="headline1">Your next listing opportunity is waiting</Typography>
        {/* @ts-expect-error props */}
        <Typography className="tw-max-w-[466px] tw-text-center">
          You don’t need to waste time managing separate logins and exporting lists from external providers
          anymore. <b>Opportunities</b> brings Expireds, FSBOs, and Pre-Foreclosures right inside your CRM, so
          your agents can <b>search, call, and convert with one seamless workflow.</b>
        </Typography>
        {/* @ts-expect-error props */}
        <Button size="medium" onClick={() => window.location.assign("/opportunities?signup")}>
          Learn More Now
        </Button>
      </div>
    );
  }

  return (
    <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[32px]">
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-[6px] tw-max-w-[580px]">
          {/* @ts-expect-error props */}
          <Typography variant="headline2">Opportunities</Typography>
          {/* @ts-expect-error props */}
          <Typography>
            Opportunities brings Expireds, FSBOs, and Pre-Foreclosures right inside your CRM, so your agents
            can search, call, and convert with one seamless workflow.
          </Typography>
        </div>
        {/* @ts-expect-error props */}
        <Button
          schema="secondary"
          size="medium"
          className="tw-whitespace-nowrap"
          onClick={() => window.open("/opportunities", "_blank")}
        >
          Launch Opportunities
        </Button>
      </div>
      <div className="tw-flex tw-gap-[32px]">
        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Plan Information</Typography>
          <div className="tw-bg-white tw-p-[24px] tw-rounded-[8px] tw-border-solid tw-border-[1.5px] tw-border-gray-10 tw-flex tw-flex-col tw-gap-[8px]">
            {/* @ts-expect-error props */}
            <Typography variant="headline3" className="tw-flex tw-justify-between">
              <span>{activeProduct?.product.name}</span>
              <span>${activeProduct?.product.details.unitPrice}</span>
            </Typography>
            {/* @ts-expect-error props */}
            <Typography variant="body-s" className="!tw-text-gray-50">
              Access Expired, FSBO, & Pre-Foreclosure data for all seats
            </Typography>
          </div>
          <div className="tw-flex tw-justify-end tw-gap-[8px]">
            <TextButton
              schema="sentence"
              size="small"
              className={`!tw-text-12d !tw-text-gray-50 ${activeProduct?.endedAt ? "" : "tw-underline"}`}
              onClick={() => setIsCancelModalOpen(true)}
              disabled={activeProduct?.endedAt !== null}
            >
              {activeProduct?.endedAt
                ? `Ends ${dayjs(activeProduct.endedAt).format("MM/DD/YYYY")}`
                : "Cancel Plan"}
            </TextButton>
          </div>
        </div>
        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Billing & Payment History</Typography>
          <div className="tw-bg-white tw-p-[24px] tw-rounded-[8px] tw-border-solid tw-border-[1.5px] tw-border-gray-10 tw-flex tw-justify-between tw-items-center tw-gap-[16px]">
            {/* @ts-expect-error props */}
            <Typography variant="body-s" className="!tw-text-gray-50 tw-max-w-[260px]">
              Manage stored credit card information, download invoices and update your account details in your
              Billing Portal
            </Typography>
            {/* @ts-expect-error props */}
            <Button
              schema="secondary"
              className="tw-whitespace-nowrap"
              onClick={() => window.open(portalUrl, "_blank")}
            >
              Go to Billing Portal
            </Button>
          </div>
        </div>
      </div>
      <OpportunitiesCancelModal
        accountProduct={activeProduct}
        onCancel={actions.cancelProduct}
        onClose={() => setIsCancelModalOpen(false)}
        open={isCancelModalOpen}
      />
    </div>
  );
};

export default PhoneSystem;
